// The calc library's JSON commands and answers (server/src/calc/wasm_api.c,
// dev-setup 15.1), typed for the Web Worker (worker.ts) and its client
// (calcClient.ts). Rows and columns are from 0; sheets are named.

/** A cell's value: a number, text, true/false, empty (null) or an error. */
export type CalcValue = number | string | boolean | null | { error: string }

export interface CalcDimItems {
  name: string
  items: string[]
}

/** What `eval` gives: a value, a grid array or a tensor (row-major). */
export type EvalValue = CalcValue | { array: CalcValue[][] } | { dims: CalcDimItems[]; values: CalcValue[] }

/** [sheet, row, col, value, flags]: a cell whose value or flags changed. */
export type CalcChange = [sheet: string, row: number, col: number, value: CalcValue, flags: number]

/** [row, col, input, value, flags] of `get`. */
export type CalcCell = [row: number, col: number, input: string, value: CalcValue, flags: number]

/** book_flag bits */
export const FLAG = {
  formula: 1,
  circular: 2,
  spill: 4,
  blocked: 8,
  spilled: 16,
  volatile: 32,
  unparsed: 64,
  tensor: 128,
} as const

type R = { recalc?: boolean }

export type CalcCommand =
  | ({ cmd: 'set_cells'; cells: [sheet: string, row: number, col: number, input: string][] } & R)
  | ({ cmd: 'add_sheet'; name: string } & R)
  | ({ cmd: 'rename_sheet'; from: string; to: string } & R)
  | ({ cmd: 'remove_sheet'; name: string } & R)
  | { cmd: 'sheets' }
  | ({ cmd: 'shift'; sheet: string; axis: 'row' | 'col'; at: number; count: number } & R)
  | ({ cmd: 'set_names'; names: [name: string, formula: string][] } & R)
  | ({ cmd: 'set_dims'; dims: { name: string; items: string[] | null }[] } & R)
  | ({ cmd: 'set_table'; name: string; columns: string[] | null; rows?: CalcValue[][] } & R)
  | ({ cmd: 'set_tensor'; name: string; dims: (string | CalcDimItems)[] | null; values?: CalcValue[] } & R)
  | ({ cmd: 'set_clock'; today: number; now: number } & R)
  | { cmd: 'recalc'; all?: boolean }
  | { cmd: 'get'; sheet: string; r1: number; c1: number; r2: number; c2: number }
  | { cmd: 'eval'; formula: string; sheet?: string; row?: number; col?: number }
  | { cmd: 'translate'; formula: string; drow: number; dcol: number }
  | { cmd: 'format'; value: CalcValue; format: string }
  | { cmd: 'reset' }

export interface CalcAnswer {
  ok: boolean
  error?: string
  changes?: CalcChange[]
  reload?: string[]
  evaluated?: number
  /** items of a list command that failed (the rest were done) */
  errors?: { i: number; error: string }[]
  cells?: CalcCell[]
  value?: EvalValue
  formula?: string
  text?: string
  sheets?: string[]
}

/** main thread -> worker: commands run in order, answered together */
export interface CalcRequest {
  id: number
  cmds: CalcCommand[]
}

/** worker -> main thread */
export type CalcReply = { id: number; answers: CalcAnswer[] } | { id: number; fatal: string }
